
import { useState } from "react";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, KeyRound, CheckCircle, XCircle, AlertCircle } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { setProviderApiKey } from "@/services/admin";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface ApiKeyManagerProps {
  provider: string;
  providerName: string;
  isConnected: boolean;
  onKeyUpdated: () => void;
}

const ApiKeyManager = ({ 
  provider, 
  providerName, 
  isConnected, 
  onKeyUpdated 
}: ApiKeyManagerProps) => {
  const [open, setOpen] = useState(false);
  const [apiKey, setApiKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!apiKey.trim()) {
        setError("API Key is required");
        toast({
          title: "API Key Required",
          description: "Please enter a valid API key",
          variant: "destructive"
        });
        return;
      }
      
      const result = await setProviderApiKey(provider, apiKey);
      
      if (result && result.success) {
        toast({
          title: "API Key Updated",
          description: `${providerName} API key has been updated successfully.`
        });
        setApiKey("");
        setOpen(false);
        onKeyUpdated();
      } else {
        throw new Error(result?.error || "Unknown error occurred");
      }
    } catch (error) {
      console.error(`Error setting ${providerName} API key:`, error);
      const errorMessage = error instanceof Error ? error.message : "An unknown error occurred";
      setError(errorMessage);
      toast({
        title: "Error Setting API Key",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button 
          variant={isConnected ? "outline" : "default"} 
          size="sm" 
          className="ml-2"
        >
          {isConnected ? (
            <>
              <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
              Connected
            </>
          ) : (
            <>
              <KeyRound className="h-4 w-4 mr-2" />
              Set API Key
            </>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Set {providerName} API Key</DialogTitle>
          <DialogDescription>
            Enter your {providerName} API key to connect to the service.
            This will be securely stored in Supabase.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {error}
              </AlertDescription>
            </Alert>
          )}
          <div className="grid gap-2">
            <Label htmlFor="apiKey">{providerName} API Key</Label>
            <Input 
              id="apiKey" 
              value={apiKey} 
              onChange={(e) => setApiKey(e.target.value)} 
              placeholder={`Enter your ${providerName} API key`}
              type="password"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save API Key
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ApiKeyManager;
